import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { clampNum } from '@muzly/voice-engine';

/**
 * BackingWaveform — 노래 진단 상단의 **반주 재생 위치**. 파형 위를 커서가 지나간다.
 * 반주가 숨은 `<audio>`로만 나가서 화면에 "재생 중"이라는 신호가 없던 자리를 채운다.
 *
 * **숫자는 적지 않는다.** 예전에는 옆에 `경과 / 전체`가 붙어 있었는데, 바로 아래 큰
 * 시계가 `경과 / 제한`으로 같은 모양의 숫자 둘을 이미 말한다 — 시안 `1677:17425`에도
 * 이 자리 숫자는 없다. 재생 위치는 커서가 말한다.
 *
 * **위치는 `<audio>`의 `currentTime`을 읽기만 한다.** 재생을 거는 것은 `SongReferenceAudio`
 * 하나이고(그 순간이 채점 앵커다), 여기서 되감거나 틀면 앵커가 흔들린다. 반주가 거절돼
 * 안 나오면 커서는 0에 머문다 — 녹음 경과 시계로 커서를 몰면 안 나오는 반주를 "재생 중"이라고
 * 그리게 된다.
 *
 * `currentTime`은 WebView 시계라 곡선(네이티브 캡처 시계)과 도메인이 다르다(ADR-0004) —
 * 그래서 이 컴포넌트는 장식이고, 곡선·채점과 값을 주고받지 않는다.
 *
 * **React state 로 올리지 않는다.** 재생 중에는 자기 rAF 에서 DOM 스타일·텍스트를 직접 고친다 —
 * 60Hz setState 는 47Hz 로 다시 그리는 곡선과 같은 트리를 흔든다.
 */

interface BackingWaveformProps {
  /** 파형 봉우리(0..1). 비어 있으면 진행 선만 그린다. */
  peaks: number[];
  /** 반주 길이(ms) — 커서 비율의 분모. */
  durationMs: number;
  audioRef: RefObject<HTMLAudioElement | null>;
  /** 반주가 도는 구간(녹음 중). 아니면 커서를 처음에 둔다. */
  playing: boolean;
}

const Bars = ({ peaks, tone }: { peaks: number[]; tone: string }) =>
  peaks.length > 0 ? (
    <div className="absolute inset-0 flex items-center gap-px">
      {peaks.map((peak, i) => (
        <span
          // 봉우리는 로드 뒤 바뀌지 않는다 — 순번이 곧 자리다.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          data-testid="backing-bar"
          className={`flex-1 rounded-full ${tone}`}
          style={{ height: `${Math.max(8, peak * 100)}%` }}
        />
      ))}
    </div>
  ) : (
    <div
      className={`absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full ${tone}`}
    />
  );

export const BackingWaveform = ({
  peaks,
  durationMs,
  audioRef,
  playing,
}: BackingWaveformProps) => {
  const { t } = useTranslation();
  const playedRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const totalSec = durationMs / 1000;

  useEffect(() => {
    const draw = (sec: number) => {
      const pct = totalSec > 0 ? clampNum(sec / totalSec, 0, 1) * 100 : 0;
      if (playedRef.current) {
        playedRef.current.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      }
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translateX(${pct}%)`;
      }
    };
    if (!playing) {
      draw(0);
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      draw(audioRef.current?.currentTime ?? 0);
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [playing, audioRef, totalSec]);

  return (
    <div data-testid="backing-waveform" className="w-full px-5">
      <div
        role="img"
        aria-label={t('web.diagnosis.record.backingLabel')}
        // 커서 상자는 폭 100% 라 끝에서 파형 폭만큼 밖으로 밀린다 — 잘라 둔다.
        className="relative h-7 flex-1 overflow-hidden"
      >
        <Bars peaks={peaks} tone="bg-[var(--bk-100)]" />
        <div
          ref={playedRef}
          data-testid="backing-played"
          className="absolute inset-0"
          style={{ clipPath: 'inset(0 100% 0 0)' }}
        >
          <Bars peaks={peaks} tone="bg-primary" />
        </div>
        {/* 폭 100% 상자를 밀어야 `translateX(%)`가 파형 폭 기준이 된다. */}
        <div
          ref={cursorRef}
          data-testid="backing-cursor"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 will-change-transform"
        >
          <span className="absolute inset-y-0 left-0 w-0.5 -translate-x-1/2 rounded-full bg-primary" />
        </div>
      </div>
    </div>
  );
};
